<template>
    <div class="appointment-calendar">
        <b-card no-body class="apt-cal-card">
            <div class="apt-cal-layout">
                <!-- ───────────── Sidebar ───────────── -->
                <div class="apt-sidebar" :class="{'apt-sidebar--open': sidebarOpen}">
                    <b-button variant="primary" block class="apt-new-btn" @click="openCreate()">
                        <feather-icon icon="PlusIcon" size="16" class="mr-25"/>
                        {{ msg('New appointment') }}
                    </b-button>

                    <!-- Pending booking requests from the landing page -->
                    <div v-if="pendingRequests.length" class="apt-side-section apt-requests">
                        <div class="apt-side-title">
                            <feather-icon icon="BellIcon" size="13" class="mr-25"/>
                            {{ msg('Pending requests') }}
                            <b-badge variant="danger" pill class="ml-auto">{{ pendingRequests.length }}</b-badge>
                        </div>
                        <div v-for="r in pendingRequests" :key="r.id" class="apt-request-card">
                            <div class="apt-request-top" @click="onRequestClick(r)">
                                <span class="apt-request-avatar">{{ initialsOf(r.contactName || r.title) }}</span>
                                <div class="apt-request-info">
                                    <div class="apt-request-name">{{ r.contactName || r.title }}</div>
                                    <div class="apt-request-meta">
                                        <feather-icon icon="ClockIcon" size="12"/>
                                        <span>{{ formatRequestDate(r.start) }}</span>
                                    </div>
                                    <div v-if="r.phoneNumber" class="apt-request-meta">
                                        <feather-icon icon="PhoneIcon" size="12"/>
                                        <span>{{ r.phoneNumber }}</span>
                                    </div>
                                </div>
                            </div>
                            <div class="apt-request-actions">
                                <b-button variant="success" size="sm"
                                          :disabled="processingRequestId === r.id"
                                          @click="acceptRequest(r)">
                                    <feather-icon icon="CheckIcon" size="13" class="mr-25"/>{{ msg('Accept') }}
                                </b-button>
                                <b-button variant="outline-danger" size="sm"
                                          :disabled="processingRequestId === r.id"
                                          @click="declineRequest(r)">
                                    <feather-icon icon="XIcon" size="13" class="mr-25"/>{{ msg('Decline') }}
                                </b-button>
                            </div>
                        </div>
                    </div>

                    <!-- Type filters / legend -->
                    <div class="apt-side-section">
                        <div class="apt-side-title">
                            {{ msg('Appointment type') }}
                            <b-link class="apt-side-toggle-all" @click="toggleAllTypes">
                                {{ allTypesSelected ? msg('None') : msg('All') }}
                            </b-link>
                        </div>
                        <div v-for="t in types" :key="t.value" class="apt-filter-row" @click="toggleType(t.value)">
                            <span class="apt-filter-check" :class="{checked: selectedTypes.includes(t.value)}"
                                  :style="{'--apt-c': t.color}">
                                <feather-icon v-if="selectedTypes.includes(t.value)" icon="CheckIcon" size="11"/>
                            </span>
                            <span class="apt-filter-label">{{ msg(t.label) }}</span>
                            <span class="apt-filter-duration">{{ t.duration }}'</span>
                        </div>
                    </div>

                    <!-- Mode filters -->
                    <div class="apt-side-section">
                        <div class="apt-side-title">{{ msg('Appointment mode') }}</div>
                        <div class="apt-mode-chips">
                            <button v-for="m in modes" :key="m.value" type="button"
                                    class="apt-mode-chip" :class="{active: selectedModes.includes(m.value)}"
                                    @click="toggleMode(m.value)">
                                <feather-icon :icon="m.icon" size="13" class="mr-25"/>{{ msg(m.label) }}
                            </button>
                        </div>
                    </div>

                    <!-- Status filters (semantic colors, distinct from the mode chips) -->
                    <div class="apt-side-section">
                        <div class="apt-side-title">{{ msg('Status') }}</div>
                        <div class="apt-mode-chips">
                            <button v-for="s in statuses" :key="s.value" type="button"
                                    class="apt-mode-chip apt-status-chip"
                                    :class="[{active: selectedStatuses.includes(s.value)}, 'apt-status-chip--' + s.value.toLowerCase()]"
                                    @click="toggleStatus(s.value)">
                                {{ msg(s.label) }}
                            </button>
                        </div>
                    </div>

                    <!-- Admin scope -->
                    <div v-if="isAdmin" class="apt-side-section apt-admin-section">
                        <div class="apt-side-title">
                            <feather-icon icon="ShieldIcon" size="13" class="mr-25"/>{{ msg('Admin view') }}
                        </div>
                        <div class="apt-switch-row">
                            <b-form-checkbox v-model="showOthers" switch class="apt-switch"/>
                            <span class="apt-switch-text" @click="showOthers = !showOthers">
                                {{ msg("Show other users' appointments") }}
                            </span>
                        </div>
                        <template v-if="showOthers">
                            <label class="apt-label mt-1">{{ msg('Branches') }}</label>
                            <v-select v-model="filterBranchIds" :options="branchOptions" :reduce="o => o.id"
                                      label="name" multiple append-to-body
                                      :placeholder="msg('All branches')"/>
                            <label class="apt-label mt-1">{{ msg('Users') }}</label>
                            <v-select v-model="filterUserIds" :options="userOptions" :reduce="o => o.id"
                                      label="name" multiple append-to-body
                                      :placeholder="msg('All users')"/>
                        </template>
                    </div>
                </div>

                <!-- ───────────── Calendar ───────────── -->
                <div class="apt-main">
                    <div class="apt-main-toolbar">
                        <b-button variant="flat-secondary" class="btn-icon d-lg-none" @click="sidebarOpen = !sidebarOpen">
                            <feather-icon icon="MenuIcon" size="18"/>
                        </b-button>
                        <div class="apt-count" v-if="!loading">
                            <b-badge variant="light-primary">{{ visibleCount }} {{ msg('appointments') }}</b-badge>
                        </div>
                        <b-spinner v-else small class="text-primary"/>
                    </div>
                    <full-calendar ref="calendar" :options="calendarOptions">
                        <template v-slot:eventContent="arg">
                            <div class="apt-event" :title="arg.event.title">
                                <feather-icon :icon="modeIcon(arg.event.extendedProps.mode)" size="12" class="apt-event-icon"/>
                                <span class="apt-event-time">{{ arg.timeText }}</span>
                                <span class="apt-event-title">{{ arg.event.title }}</span>
                            </div>
                        </template>
                    </full-calendar>
                </div>
            </div>
        </b-card>

        <appointment-modal v-model="showModal" :appointment="selectedAppointment"
                           :defaults="createDefaults" @saved="refetch" @deleted="refetch"/>
    </div>
</template>

<script>
import FullCalendar from '@fullcalendar/vue'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import listPlugin from '@fullcalendar/list'
import interactionPlugin from '@fullcalendar/interaction'
import allLocales from '@fullcalendar/core/locales-all'
import vSelect from 'vue-select'
import AppointmentModal from '@/components/modals/appointment/AppointmentModal.vue'
import {
    APPOINTMENT_MODES, APPOINTMENT_TYPES, APPOINTMENT_STATUSES,
    appointmentType, appointmentMode,
} from '@/model/appointment/appointmentEnums'

export default {
    name: 'AppointmentCalendar',
    components: {FullCalendar, vSelect, AppointmentModal},
    data() {
        return {
            loading: false,
            sidebarOpen: false,
            showModal: false,
            selectedAppointment: null,
            createDefaults: null,
            visibleCount: 0,
            // appointments of the current range, by id (for edit)
            byId: {},

            modes: APPOINTMENT_MODES,
            types: APPOINTMENT_TYPES,
            statuses: APPOINTMENT_STATUSES,

            selectedTypes: APPOINTMENT_TYPES.map((t) => t.value),
            selectedModes: APPOINTMENT_MODES.map((m) => m.value),
            // declined requests are hidden by default (they only matter as history)
            selectedStatuses: APPOINTMENT_STATUSES.map((s) => s.value).filter((s) => s !== 'DECLINED'),

            // landing-page booking requests awaiting accept/decline
            pendingRequests: [],
            processingRequestId: null,

            // admin scope
            showOthers: false,
            filterBranchIds: [],
            filterUserIds: [],

            calendarOptions: {
                plugins: [dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin],
                initialView: 'timeGridWeek',
                headerToolbar: {
                    start: 'prev,next today',
                    center: 'title',
                    end: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek',
                },
                locales: allLocales,
                locale: 'en',
                firstDay: 1,
                height: 'auto',
                nowIndicator: true,
                navLinks: true,
                dayMaxEvents: 4,
                editable: true,
                selectable: true,
                slotMinTime: '06:00:00',
                slotMaxTime: '22:00:00',
                scrollTime: '08:00:00',
                events: this.fetchEvents,
                select: this.onSelect,
                eventClick: this.onEventClick,
                eventDrop: this.onEventMoved,
                eventResize: this.onEventMoved,
            },
        }
    },
    computed: {
        isAdmin() {
            const p = this.$store.getters['user/myProfile'];
            return (p && p.isAdmin) || this.$store.getters['user/canOperate']('appointments');
        },
        allTypesSelected() {
            return this.selectedTypes.length === this.types.length;
        },
        branchOptions() {
            const branches = this.$store.getters['data/getAllBranches'] || {};
            return Object.values(branches).map((b) => ({id: b.id, name: b.name}));
        },
        userOptions() {
            return this.$store.getters['appointment/getAssignableUsers'] || [];
        },
        currentLang() {
            return this.$store.getters['translator/getCurrentLang'] || 'en';
        },
    },
    watch: {
        currentLang: {
            handler(lang) {
                this.calendarOptions.locale = lang || 'en';
            },
            immediate: true,
        },
        selectedTypes: 'refetch',
        selectedModes: 'refetch',
        selectedStatuses: 'refetch',
        showOthers() {
            if (this.showOthers && !this.userOptions.length) {
                this.$store.dispatch('appointment/loadAssignableUsers');
            }
            this.refetch();
        },
        filterBranchIds: 'refetch',
        filterUserIds: 'refetch',
    },
    mounted() {
        if (!this.userOptions.length) {
            this.$store.dispatch('appointment/loadAssignableUsers');
        }
        this.loadPendingRequests();
    },
    methods: {
        // FullCalendar "function" event source
        fetchEvents(fetchInfo, successCallback, failureCallback) {
            this.loading = true;
            const filter = {
                from: this.fmt(fetchInfo.start),
                to: this.fmt(fetchInfo.end),
                types: this.selectedTypes,
                modes: this.selectedModes,
                statuses: this.selectedStatuses,
                onlyMine: this.isAdmin ? !this.showOthers : true,
            };
            if (this.isAdmin && this.showOthers) {
                if (this.filterBranchIds.length) filter.buIds = this.filterBranchIds;
                if (this.filterUserIds.length) filter.userIds = this.filterUserIds;
            }
            this.$store.dispatch('appointment/fetchAll', filter).then(
                (appointments) => {
                    this.loading = false;
                    this.byId = {};
                    const events = (appointments || []).map((a) => {
                        this.byId[a.id] = a;
                        const color = a.color || appointmentType(a.type).color;
                        const muted = a.status === 'CANCELLED' || a.status === 'NO_SHOW' || a.status === 'DECLINED';
                        const classNames = [];
                        if (muted) classNames.push('apt-event--muted');
                        if (a.status === 'REQUESTED') classNames.push('apt-event--requested');
                        return {
                            id: a.id,
                            title: a.title + (a.contactName ? ' · ' + a.contactName : ''),
                            start: this.iso(a.start),
                            end: this.iso(a.end),
                            allDay: !!a.allDay,
                            backgroundColor: color,
                            borderColor: color,
                            classNames,
                            extendedProps: {mode: a.mode, type: a.type, status: a.status},
                        };
                    });
                    this.visibleCount = events.length;
                    successCallback(events);
                },
                (err) => {
                    this.loading = false;
                    failureCallback(err);
                }
            );
        },
        refetch() {
            const api = this.$refs.calendar && this.$refs.calendar.getApi();
            if (api) api.refetchEvents();
            this.loadPendingRequests();
        },

        // ── landing-page booking requests ──
        loadPendingRequests() {
            const now = new Date();
            const horizon = new Date(now.getTime() + 60 * 24 * 3600 * 1000);
            this.$store.dispatch('appointment/fetchAll', {
                statuses: ['REQUESTED'],
                from: this.fmt(now),
                to: this.fmt(horizon),
            }).then((list) => {
                this.pendingRequests = list || [];
                // keep the navbar badge in sync
                this.$store.commit('appointment/setPendingCount', this.pendingRequests.length);
            }).catch(() => { /* errors already notified by the store */ });
        },
        onRequestClick(r) {
            this.selectedAppointment = r;
            this.createDefaults = null;
            this.showModal = true;
        },
        async acceptRequest(r) {
            this.processingRequestId = r.id;
            try {
                await this.$store.dispatch('appointment/acceptRequest', r.id);
                this.$store.dispatch('notifications/success', {
                    title: this.msg('Calendar'),
                    message: this.msg('Request accepted — the lead has been notified'),
                }, {root: true});
                this.refetch();
            } catch (e) { /* handled by store */
            } finally {
                this.processingRequestId = null;
            }
        },
        async declineRequest(r) {
            const yes = await this.$bvModal.msgBoxConfirm(
                this.msg('Decline this request? The lead will be asked to pick another time.'), {
                    title: this.msg('Decline'),
                    okVariant: 'danger',
                    okTitle: this.msg('Decline'),
                    cancelTitle: this.msg('Cancel'),
                    centered: true,
                });
            if (!yes) return;
            this.processingRequestId = r.id;
            try {
                await this.$store.dispatch('appointment/declineRequest', r.id);
                this.$store.dispatch('notifications/success', {
                    title: this.msg('Calendar'),
                    message: this.msg('Request declined — the lead has been asked to rebook'),
                }, {root: true});
                this.refetch();
            } catch (e) { /* handled by store */
            } finally {
                this.processingRequestId = null;
            }
        },
        initialsOf(name) {
            if (!name) return '?';
            return String(name).split(' ').filter(Boolean).slice(0, 2)
                .map((p) => p[0].toUpperCase()).join('');
        },
        formatRequestDate(s) {
            if (!s) return '';
            const d = new Date(String(s).replace(' ', 'T'));
            if (isNaN(d.getTime())) return '';
            return d.toLocaleString(undefined, {weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'});
        },

        // ── interactions ──
        openCreate(defaults) {
            this.selectedAppointment = null;
            this.createDefaults = defaults || null;
            this.showModal = true;
        },
        // fires on click and on click-drag selection (selectable: true)
        onSelect(info) {
            if (!this.canCreate()) return;
            if (info.allDay) {
                const start = new Date(info.start.getTime());
                start.setHours(9, 0, 0, 0);
                this.openCreate({start: this.fmt(start)});
                return;
            }
            this.openCreate({start: this.fmt(info.start), end: this.fmt(info.end)});
        },
        onEventClick(info) {
            const a = this.byId[info.event.id];
            if (!a) return;
            this.selectedAppointment = a;
            this.createDefaults = null;
            this.showModal = true;
        },
        // drag & drop / resize → persist new times
        onEventMoved(info) {
            const a = this.byId[info.event.id];
            if (!a) {
                info.revert();
                return;
            }
            const payload = {
                start: this.fmt(info.event.start),
                end: this.fmt(info.event.end || new Date(info.event.start.getTime() + 30 * 60000)),
            };
            this.$store.dispatch('appointment/update', {id: a.id, appointment: payload}).then(
                () => this.refetch(),
                () => info.revert()
            );
        },
        canCreate() {
            return this.$store.getters['user/canCreate']('appointments');
        },

        // ── filters ──
        toggleType(v) {
            const i = this.selectedTypes.indexOf(v);
            if (i >= 0) this.selectedTypes.splice(i, 1);
            else this.selectedTypes.push(v);
        },
        toggleAllTypes() {
            this.selectedTypes = this.allTypesSelected ? [] : this.types.map((t) => t.value);
        },
        toggleMode(v) {
            const i = this.selectedModes.indexOf(v);
            if (i >= 0) this.selectedModes.splice(i, 1);
            else this.selectedModes.push(v);
        },
        toggleStatus(v) {
            const i = this.selectedStatuses.indexOf(v);
            if (i >= 0) this.selectedStatuses.splice(i, 1);
            else this.selectedStatuses.push(v);
        },

        // ── helpers ──
        modeIcon(mode) {
            return appointmentMode(mode).icon;
        },
        // Date → "yyyy-MM-dd HH:mm" (backend format)
        fmt(d) {
            const p = (n) => String(n).padStart(2, '0');
            return d.getFullYear() + '-' + p(d.getMonth() + 1) + '-' + p(d.getDate())
                + ' ' + p(d.getHours()) + ':' + p(d.getMinutes());
        },
        // "yyyy-MM-dd HH:mm" → ISO-ish string FullCalendar accepts
        iso(s) {
            return s ? String(s).replace(' ', 'T') : s;
        },
    },
}
</script>

<style lang="scss">
/* FullCalendar v6 injects its base styles automatically — only the skin below is ours. */
.appointment-calendar {
    .apt-cal-card {
        overflow: visible;
    }

    .apt-cal-layout {
        display: flex;
        min-height: 70vh;
    }

    /* ── sidebar ── */
    .apt-sidebar {
        flex: 0 0 250px;
        max-width: 250px;
        padding: 1.25rem 1rem;
        border-right: 1px solid #ebe9f1;

        @media (max-width: 991px) {
            display: none;

            &.apt-sidebar--open {
                display: block;
                position: absolute;
                z-index: 10;
                background: #fff;
                box-shadow: 0 6px 24px rgba(34, 41, 47, .15);
                border-radius: .5rem;
                border-right: none;
            }
        }
    }

    .apt-new-btn {
        background: linear-gradient(120deg, #7367f0, #9e95f5) !important;
        border: none !important;
        box-shadow: 0 4px 14px rgba(115, 103, 240, .4);
        font-weight: 600;
    }

    .apt-side-section {
        margin-top: 1.5rem;
    }

    .apt-side-title {
        display: flex;
        align-items: center;
        font-size: .78rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: .4px;
        color: #a6a4b0;
        margin-bottom: .6rem;
    }

    .apt-side-toggle-all {
        margin-left: auto;
        font-size: .72rem;
        text-transform: none;
        letter-spacing: 0;
    }

    .apt-filter-row {
        display: flex;
        align-items: center;
        gap: .5rem;
        padding: .3rem .25rem;
        border-radius: .35rem;
        cursor: pointer;
        user-select: none;

        &:hover {
            background: #f8f8fb;
        }
    }

    .apt-filter-check {
        width: 16px;
        height: 16px;
        flex: 0 0 16px;
        border-radius: 4px;
        border: 2px solid var(--apt-c);
        display: flex;
        align-items: center;
        justify-content: center;
        color: #fff;
        transition: all .12s ease;

        &.checked {
            background: var(--apt-c);
        }
    }

    .apt-filter-label {
        flex: 1;
        font-size: .85rem;
        color: #5e5873;
    }

    .apt-filter-duration {
        font-size: .72rem;
        color: #b9b9c3;
    }

    .apt-mode-chips {
        display: flex;
        flex-wrap: wrap;
        gap: .4rem;
    }

    .apt-mode-chip {
        display: inline-flex;
        align-items: center;
        padding: .3rem .6rem;
        border-radius: 2rem;
        border: 1px solid #d8d6de;
        background: transparent;
        color: #6e6b7b;
        font-size: .75rem;
        font-weight: 600;
        cursor: pointer;
        transition: all .12s ease;

        &.active {
            background: #7367f0;
            border-color: #7367f0;
            color: #fff;
        }
    }

    .apt-admin-section {
        border-top: 1px dashed #ebe9f1;
        padding-top: 1.25rem;

        .apt-label {
            font-size: .78rem;
            font-weight: 600;
            color: #5e5873;
            display: block;
            margin-bottom: .25rem;
        }
    }

    /* admin switch: toggle and text side by side — no overlap on wrap.
       The theme's switch knob overflows its layout box (~3.2rem visual width
       vs 2.25rem box), so reserve the real width explicitly. */
    .apt-switch-row {
        display: flex;
        align-items: flex-start;
    }

    .apt-switch {
        flex: 0 0 3.4rem;
        width: 3.4rem;
    }

    .apt-switch-text {
        font-size: .85rem;
        color: #5e5873;
        line-height: 1.5;
        cursor: pointer;
        user-select: none;
    }

    /* status chips: semantic colors so they don't read like the mode chips */
    .apt-status-chip.active {
        box-shadow: none;

        &.apt-status-chip--requested { background: #ff9f43; border-color: #ff9f43; }
        &.apt-status-chip--scheduled { background: #7367f0; border-color: #7367f0; }
        &.apt-status-chip--completed { background: #28c76f; border-color: #28c76f; }
        &.apt-status-chip--cancelled { background: #82868b; border-color: #82868b; }
        &.apt-status-chip--no_show { background: #ea5455; border-color: #ea5455; }
        &.apt-status-chip--declined { background: #b52d2e; border-color: #b52d2e; }
    }

    /* ── main ── */
    .apt-main {
        flex: 1;
        min-width: 0;
        padding: 1.25rem 1.4rem;
        position: relative;
    }

    .apt-main-toolbar {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: .5rem;
        margin-bottom: .5rem;
        min-height: 24px;
    }

    /* ── FullCalendar skin ── */
    .fc {
        --fc-border-color: #ebe9f1;
        --fc-today-bg-color: rgba(115, 103, 240, .06);
        --fc-page-bg-color: transparent;
        font-size: .88rem;
    }

    .fc .fc-toolbar-title {
        font-size: 1.15rem;
        font-weight: 700;
        color: #5e5873;
    }

    .fc .fc-button {
        text-transform: capitalize;
        border-radius: .4rem;
        padding: .35rem .7rem;
    }

    .fc .fc-button-primary {
        background: transparent;
        border-color: #7367f0;
        color: #7367f0;

        &:hover, &.fc-button-active {
            background: #7367f0 !important;
            border-color: #7367f0 !important;
            color: #fff !important;
        }
    }

    /* view-switcher buttons — app-calendar.scss paints them tan (#c2aa8e)
       with !important at high specificity, so match its selector shape */
    .fc .fc-toolbar .fc-button-group .fc-button-primary:not(.fc-prev-button):not(.fc-next-button) {
        color: #7367f0;
        background-color: transparent !important;
        border-color: #7367f0 !important;
    }

    .fc .fc-toolbar .fc-button-group .fc-button-primary:not(.fc-prev-button):not(.fc-next-button):hover {
        color: #7367f0;
        background-color: rgba(115, 103, 240, .12) !important;
        border-color: #7367f0 !important;
    }

    .fc .fc-toolbar .fc-button-group .fc-button-primary:not(.fc-prev-button):not(.fc-next-button).fc-button-active {
        color: #fff;
        background-color: #7367f0 !important;
        border-color: #7367f0 !important;
    }

    /* today highlight — the @core theme (app-calendar.scss) forces
       .fc-day-today to $body-bg with !important, so we out-specify it */
    .fc td.fc-day-today,
    .fc .fc-daygrid-day.fc-day-today,
    .fc .fc-timegrid-col.fc-day-today,
    .fc .fc-day-today:not(.fc-timeline-slot):not(.fc-col-header-cell) {
        background: rgba(115, 103, 240, .12) !important;
        background-color: rgba(115, 103, 240, .12) !important;
        box-shadow: inset 0 0 0 1px rgba(115, 103, 240, .25);
    }

    .fc th.fc-day-today,
    .fc .fc-col-header-cell.fc-day-today {
        background: rgba(115, 103, 240, .22) !important;
        background-color: rgba(115, 103, 240, .22) !important;

        .fc-col-header-cell-cushion {
            color: #7367f0;
            font-weight: 700;
        }
    }

    /* today's date number as a filled badge */
    .fc .fc-day-today .fc-daygrid-day-number {
        background: #7367f0;
        color: #fff !important;
        border-radius: 50%;
        min-width: 1.6rem;
        height: 1.6rem;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-weight: 700;
        line-height: 1;
    }

    .fc .fc-event {
        border-radius: .35rem;
        padding: 1px 4px;
        cursor: pointer;
        border: none;
    }

    .apt-event {
        display: flex;
        align-items: center;
        gap: .3rem;
        overflow: hidden;
        color: #fff;
        line-height: 1.35;
        width: 100%;
    }

    .apt-event-icon {
        flex: 0 0 auto;
    }

    .apt-event-time {
        font-size: .7rem;
        opacity: .9;
        flex: 0 0 auto;
    }

    .apt-event-title {
        font-weight: 600;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .fc .apt-event--muted {
        opacity: .45;

        .apt-event-title {
            text-decoration: line-through;
        }
    }

    /* landing-page requests: dashed outline until accepted */
    .fc .apt-event--requested {
        border: 2px dashed rgba(255, 255, 255, .9) !important;
        opacity: .85;

        .apt-event-title::after {
            content: ' ?';
            font-weight: 800;
        }
    }

    /* ── pending requests panel ── */
    .apt-requests {
        margin-top: 1.5rem;

        .apt-side-title {
            color: #ff9f43;
        }
    }

    .apt-request-card {
        border: 1px solid #ebe9f1;
        border-left: 4px solid #ff9f43;
        border-radius: .65rem;
        background: #fff;
        padding: .7rem .75rem .6rem;
        margin-bottom: .6rem;
        box-shadow: 0 3px 10px rgba(34, 41, 47, .06);
        transition: transform .12s ease, box-shadow .12s ease;

        &:hover {
            transform: translateY(-1px);
            box-shadow: 0 6px 16px rgba(34, 41, 47, .12);
        }

        &:last-child {
            margin-bottom: 0;
        }
    }

    .apt-request-top {
        display: flex;
        gap: .6rem;
        cursor: pointer;
    }

    .apt-request-avatar {
        width: 34px;
        height: 34px;
        flex: 0 0 34px;
        border-radius: 50%;
        background: linear-gradient(135deg, #ff9f43, #ffc085);
        color: #fff;
        font-weight: 700;
        font-size: .75rem;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .apt-request-info {
        flex: 1;
        min-width: 0;
    }

    .apt-request-name {
        font-weight: 700;
        font-size: .88rem;
        color: #5e5873;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        margin-bottom: .1rem;
    }

    .apt-request-meta {
        display: flex;
        align-items: center;
        gap: .3rem;
        font-size: .75rem;
        color: #6e6b7b;
        line-height: 1.5;
    }

    .apt-request-actions {
        display: flex;
        gap: .5rem;
        margin-top: .6rem;

        .btn {
            flex: 1;
            padding: .35rem .5rem;
            font-size: .75rem;
        }
    }

    /* list view text color */
    .fc .fc-list-event .apt-event {
        color: inherit;
    }
}

/* ── dark mode ── */
.dark-layout .appointment-calendar {
    .apt-sidebar {
        border-right-color: #3b4253;

        @media (max-width: 991px) {
            &.apt-sidebar--open {
                background: #283046;
            }
        }
    }

    .apt-filter-row:hover {
        background: #343d55;
    }

    .apt-filter-label {
        color: #d0d2d6;
    }

    .apt-mode-chip {
        border-color: #3b4253;
        color: #b4b7bd;

        &.active {
            color: #fff;
        }
    }

    .apt-admin-section {
        border-top-color: #3b4253;

        .apt-label {
            color: #d0d2d6;
        }
    }

    .apt-switch-text {
        color: #d0d2d6;
    }

    .apt-request-card {
        background: #283046;
        border-color: #3b4253;
        border-left-color: #ff9f43;
        box-shadow: 0 3px 10px rgba(0, 0, 0, .25);

        &:hover {
            box-shadow: 0 6px 16px rgba(0, 0, 0, .4);
        }
    }

    .apt-request-name {
        color: #d0d2d6;
    }

    .apt-request-meta {
        color: #b4b7bd;
    }

    .fc {
        --fc-border-color: #3b4253;
        --fc-today-bg-color: rgba(115, 103, 240, .12);
        --fc-list-event-hover-bg-color: #343d55;
        --fc-neutral-bg-color: #283046;
    }

    /* dark grid lines — app-calendar.scss sets td/th borders to the LIGHT
       $border-color and only corrects it for the legacy .app-calendar page */
    .fc th,
    .fc td,
    .fc tbody td,
    .fc thead th,
    .fc .fc-scrollgrid,
    .fc .fc-list {
        border-color: #3b4253;
    }

    /* "+N more" day popover */
    .fc .fc-popover {
        background: #283046;
        border-color: #3b4253;
    }

    .fc .fc-popover-header {
        background: #343d55;
        color: #d0d2d6;
    }

    .fc .fc-popover-body .fc-daygrid-event {
        color: #d0d2d6;
    }

    /* today highlight — app-calendar.scss paints .fc-day-today with the LIGHT
       $body-bg (!important, theme-independent), which shows as white in dark
       mode. Out-specify it and set both background longhand + shorthand. */
    .fc td.fc-day-today,
    .fc .fc-daygrid-day.fc-day-today,
    .fc .fc-timegrid-col.fc-day-today,
    .fc .fc-day-today:not(.fc-timeline-slot):not(.fc-col-header-cell),
    .fc .fc-timegrid .fc-scrollgrid-section .fc-day-today {
        background: rgba(115, 103, 240, .20) !important;
        background-color: rgba(115, 103, 240, .20) !important;
        box-shadow: inset 0 0 0 1px rgba(115, 103, 240, .35);
    }

    .fc th.fc-day-today,
    .fc .fc-col-header-cell.fc-day-today,
    .fc .fc-timegrid .fc-scrollgrid-section .fc-day-today.fc-col-header-cell {
        background: rgba(115, 103, 240, .32) !important;
        background-color: rgba(115, 103, 240, .32) !important;

        .fc-col-header-cell-cushion {
            color: #beb3f8;
            font-weight: 700;
        }
    }

    .fc .fc-toolbar-title,
    .fc .fc-col-header-cell-cushion,
    .fc .fc-daygrid-day-number,
    .fc .fc-list-day-text,
    .fc .fc-list-day-side-text,
    .fc .fc-list-event-title,
    .fc .fc-list-event-time,
    .fc .fc-timegrid-slot-label,
    .fc .fc-timegrid-axis-cushion {
        color: #d0d2d6;
    }

    .fc .fc-list-day-cushion {
        background: #343d55;
    }
}
</style>
